"use client"
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import Footer from '../../components/common/Footer';

export default function LocalMovingVilniusPage() {
  // State for visibility animations
  const [isHeroVisible, setIsHeroVisible] = useState(false);
  const [isServicesVisible, setIsServicesVisible] = useState(false);
  const [isWhyChooseUsVisible, setIsWhyChooseUsVisible] = useState(false);
  const [isProcessVisible, setIsProcessVisible] = useState(false);
  const [isFAQVisible, setIsFAQVisible] = useState(false);
  const [isContactVisible, setIsContactVisible] = useState(false);
  
  // Interactive states
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const [activeFAQ, setActiveFAQ] = useState<number | null>(null);
  
  // Refs for intersection observers
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const whyChooseUsRef = useRef(null);
  const processRef = useRef(null);
  const faqRef = useRef(null);
  const contactRef = useRef(null);

  const t = useTranslations();

  // Setup intersection observers
  useEffect(() => {
    const createObserver = (ref: any, setVisible: any) => {
      return new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
            setVisible(true);
          }
        },
        {
          threshold: 0.02,
          rootMargin: '0px 0px 50px 0px'
        }
      );
    };

    const observers = [
      { ref: heroRef, setVisible: setIsHeroVisible },
      { ref: servicesRef, setVisible: setIsServicesVisible },
      { ref: whyChooseUsRef, setVisible: setIsWhyChooseUsVisible },
      { ref: processRef, setVisible: setIsProcessVisible },
      { ref: faqRef, setVisible: setIsFAQVisible },
      { ref: contactRef, setVisible: setIsContactVisible },
    ];

    const observerInstances = observers.map(({ ref, setVisible }) => {
      const observer = createObserver(ref, setVisible);
      if (ref.current) {
        observer.observe(ref.current);
      }
      return { observer, ref };
    });

    return () => {
      observerInstances.forEach(({ observer, ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
      }
      });
    };
  }, []);

  return (
    <>
      {/* Global Styles */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes drawLine {
          0% { stroke-dasharray: 1000; stroke-dashoffset: 1000; }
          40% { stroke-dasharray: 1000; stroke-dashoffset: 0; }
          60% { stroke-dasharray: 1000; stroke-dashoffset: 0; }
          100% { stroke-dasharray: 1000; stroke-dashoffset: -1000; }
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        
        @keyframes pulse {
          0%, 100% { 
            box-shadow: 0 0 8px #D90D34, 0 0 12px #D90D34, 0 0 16px #D90D34;
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 0 12px #D90D34, 0 0 20px #D90D34, 0 0 28px #D90D34;
            transform: scale(1.1);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @media (max-width: 768px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
            text-align: center !important;
          }
          
          .hero-title {
            font-size: 32px !important;
            line-height: 1.2 !important;
          }
          
          .hero-subtitle {
            font-size: 18px !important;
            padding-left: 0 !important;
          }
          
          .hero-map {
            transform: scale(1) translateX(0%) !important;
            order: -1;
          }
          
          .services-grid {
            column-count: 1 !important;
            max-width: 100% !important;
          }
          
          .destinations-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
            text-align: center !important;
            padding: 40px 20px !important;
          }
          
          .destinations-truck-container {
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
            position: relative !important;
          }
          
          .destinations-truck-image {
            max-width: 90% !important;
            height: auto !important;
          }
          
          .destinations-mjc-blur {
            width: 300px !important;
            height: 150px !important;
            top: -30px !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
          }
          
          .why-choose-grid {
            grid-template-columns: 1fr !important;
            grid-template-rows: repeat(8, auto) !important;
            min-height: auto !important;
            gap: 20px !important;
          }
          
          .why-choose-grid > div {
            grid-area: auto !important;
          }
          
          .why-choose-box-1 {
            padding: 32px 20px 100px 20px !important;
          }
          
          .why-choose-box-1 .mjc-blur-mobile {
            width: 100% !important;
            height: 120px !important;
            left: 0 !important;
            bottom: -20px !important;
          }
          
          .why-choose-box-2 {
            flex-direction: column !important;
            text-align: center !important;
            padding: 32px 20px !important;
          }
          
          .why-choose-box-2 .text-content {
            max-width: 100% !important;
            padding-right: 0 !important;
            margin-bottom: 20px !important;
          }
          
          .why-choose-box-2 .world-container {
            position: relative !important;
            width: 100% !important;
            height: 150px !important;
            bottom: auto !important;
            right: auto !important;
          }
          
          .process-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          
          .contact-title {
            font-size: 36px !important;
          }
          
          .contact-description {
            font-size: 18px !important;
          }
          
          .hero-padding {
            padding: 130px 12px 80px 12px !important;
          }
          
          .section-padding {
            padding: 80px 12px !important;
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 28px !important;
          }
          
          .section-title {
            font-size: 32px !important;
          }
          
          .section-padding {
            padding: 60px 16px !important;
          }
          
          .hero-padding {
            padding: 120px 16px 60px 16px !important;
          }
        }
      `}} />

    <main style={{ margin: 0, padding: 0, width: "100vw", minHeight: "100vh" }}>
        
        {/* HERO SECTION */}
        <section 
          ref={heroRef} 
          style={{
        background: "#000",
        padding: "100px 24px 80px 24px",
        position: "relative",
        overflow: "hidden",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
          }}
          className="hero-padding"
        >
          {/* Background gradients */}
          <div style={{
            position: "absolute",
            left: "50%",
            top: "0px",
            transform: "translateX(-50%)",
            width: "900px",
            height: "200px",
            background: "radial-gradient(50% 50% at 50% 50%,rgb(221, 3, 3) 60%, rgba(217, 13, 52, 0) 100%)",
            filter: "blur(150px)",
            zIndex: 1,
            opacity: 0.6,
          }} />
          
          <div style={{
            position: "absolute",
            left: "-200px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "400px",
            height: "400px",
            background: "radial-gradient(50% 50% at 50% 50%, #D90D34 0%, rgba(217, 13, 52, 0) 100%)",
            filter: "blur(150px)",
            zIndex: 1,
            opacity: 0.3,
          }} />
          
          <div style={{
            position: "absolute",
            right: "-200px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "300px",
            height: "300px",
            background: "radial-gradient(50% 50% at 50% 50%, #B51F24 0%, rgba(181, 31, 36, 0) 100%)",
            filter: "blur(120px)",
            zIndex: 1,
            opacity: 0.2,
          }} />

        <div style={{
          maxWidth: "1600px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "60px",
          alignItems: "center",
          position: "relative",
          zIndex: 10,
          width: "100%",
          }}
          className="hero-grid">

            {/* Left side - Text content */}
          <div style={{ 
            position: "relative", 
            zIndex: 100, 
            paddingRight: "20px",
              opacity: isHeroVisible ? 1 : 0,
              transform: isHeroVisible ? 'translateX(0px)' : 'translateX(-40px)',
              transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: "0.2s",
          }}>
            <h1 style={{
              fontSize: "42px",
              fontWeight: 600,
              color: "#fff",
              lineHeight: 1.1,
              marginBottom: "28px",
              letterSpacing: "-0.02em",
              }} 
              className="hero-title">
                {t('localMovingVilnius.hero.title')}
            </h1>
            
            <h2 style={{
              fontSize: "22px",
              fontWeight: 400,
              color: "#fff",
              marginBottom: "36px",
              lineHeight: 1.3,
              position: "relative",
              paddingLeft: "24px",
              }} 
              className="hero-subtitle">
              <span style={{
                position: "absolute",
                left: "0",
                top: "0",
                bottom: "0",
                width: "3px",
                background: "#F20C2C",
                borderRadius: "2px",
              }}></span>
                {t('localMovingVilnius.hero.subtitle')}
            </h2>
            
            <div style={{
              fontSize: "16px",
              color: "rgba(255,255,255,0.85)",
              lineHeight: 1.6,
                opacity: isHeroVisible ? 1 : 0,
                transform: isHeroVisible ? 'translateY(0px)' : 'translateY(30px)',
                transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.8s",
            }}>
              <p style={{ marginBottom: "24px" }}>
                  {t('localMovingVilnius.hero.paragraph1')}
              </p>
              <p>
                  {t('localMovingVilnius.hero.paragraph2')}
              </p>
            </div>
          </div>

            {/* Right side - Map image */}
          <div style={{
            position: "relative",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            zIndex: 100,
            width: "100%",
            overflow: "visible",
          }}>
              <div style={{ 
                position: "relative", 
                transform: "scale(1.4) translateX(10%)",
                width: "100%",
              }}
              className="hero-map">
              <Image
                src="/images/vilnius-moving.svg"
                alt="Vilnius Moving Services"
                width={400}
                height={300}
                style={{ 
                  width: "100%",
                  maxWidth: "500px",
                  height: "auto",
                }}
                priority
              />
                
                {/* Simple static glow effect */}
              <div 
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "120%",
                  height: "120%",
                  background: "radial-gradient(circle, rgba(217, 13, 52, 0.1) 0%, transparent 70%)",
                  pointerEvents: "none",
                  zIndex: 5,
                }}
              />
            </div>
          </div>
        </div>
      </section>

        {/* SERVICES SECTION */}
        <section 
          ref={servicesRef} 
          style={{
            background: "#000",
            padding: "80px 24px",
            position: "relative",
            overflow: "hidden",
          }}
          className="section-padding"
        >
          {/* Background gradient */}
          <div style={{
            position: "absolute",
            left: "50%",
            top: "300px",
            transform: "translateX(-50%)",
            width: "600px",
            height: "400px",
            background: "radial-gradient(50% 50% at 50% 50%, #D90D34 0%, rgba(217, 13, 52, 0) 100%)",
            filter: "blur(150px)",
            zIndex: 1,
            opacity: isServicesVisible ? 0.3 : 0,
            transition: "opacity 1.5s ease-out",
            transitionDelay: "0.5s",
          }} />

          <div style={{
            maxWidth: "900px",
            margin: "0 auto",
            position: "relative",
            zIndex: 10,
          }}>
            {/* Title and Description */}
            <div style={{ 
              textAlign: "center", 
              marginBottom: "80px",
              opacity: isServicesVisible ? 1 : 0,
              transform: isServicesVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            }}>
              <h2 style={{
                fontSize: "42px",
                fontWeight: 600,
                color: "#fff",
                lineHeight: 1.2,
                marginBottom: "32px",
                maxWidth: "1000px",
                margin: "0 auto 32px auto",
              }}
              className="section-title">
                {t('localMovingVilnius.services.title')}
              </h2>
              
              <p style={{
                fontSize: "18px",
                color: "rgba(255,255,255,0.85)",
                lineHeight: 1.6,
                maxWidth: "800px",
                margin: "0 auto",
                opacity: isServicesVisible ? 1 : 0,
                transform: isServicesVisible ? 'translateY(0px)' : 'translateY(30px)',
                transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.3s",
              }}>
                {t('localMovingVilnius.services.intro')}
              </p>
              
              <h3 style={{
                fontSize: "28px",
                fontWeight: 500,
                color: "#fff",
                marginTop: "40px",
                maxWidth: "800px",
                margin: "40px auto 0 auto",
                opacity: isServicesVisible ? 1 : 0,
                transform: isServicesVisible ? 'translateY(0px)' : 'translateY(30px)',
                transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.4s",
              }}>
                {t('localMovingVilnius.services.whatWeOffer')}
              </h3>
            </div>

            {/* Services Grid */}
            <div style={{
              columnCount: 3,
              columnGap: "32px",
              marginBottom: "32px",
              maxWidth: "900px",
              margin: "60px auto 0 auto",
              opacity: isServicesVisible ? 1 : 0,
              transform: isServicesVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.5s",
            }} 
            className="services-grid">
              
              {/* Service Box 1 - Consultation */}
              <div style={{
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "70px 24px 40px 24px",
                marginBottom: "32px",
                breakInside: "avoid",
                position: "relative",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: isServicesVisible ? 1 : 0,
                transform: isServicesVisible ? 'scale(1) translateY(0px)' : 'scale(0.95) translateY(20px)',
                transition: 'all 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.6s",
              }}>
                <div style={{
                  position: "absolute",
                  top: "16px",
                  left: "16px",
                  width: "40px",
                  height: "40px",
                }}>
                  <Image
                    src="/images/components/international/red-circle.png"
                    alt="Circle background"
                    width={40}
                    height={40}
                    style={{ position: "absolute", top: 0, left: 0 }}
                  />
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "20px",
                    height: "20px",
                  }}>
                    <div style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                    }}>
                                          <Image
                        src="/images/components/international/chat.png"
                        alt="Chat icon"
                        fill
                        unoptimized
                        style={{ 
                          objectFit: "contain"
                        }}
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#fff",
                    marginBottom: "12px",
                  }}>
                    {t('localMovingVilnius.services.servicesList.apartments.title')}
                  </h3>
                  <p style={{
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.services.servicesList.apartments.description')}
                  </p>
                </div>
              </div>

              {/* Service Box 2 - Furniture */}
              <div style={{
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "70px 24px 40px 24px",
                marginBottom: "32px",
                breakInside: "avoid",
                position: "relative",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: isServicesVisible ? 1 : 0,
                transform: isServicesVisible ? 'translateX(0px)' : 'translateX(-40px)',
                transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.7s",
              }}>
                <div style={{
                  position: "absolute",
                  top: "16px",
                  left: "16px",
                  width: "40px",
                  height: "40px",
                }}>
                  <Image
                    src="/images/components/international/red-circle.png"
                    alt="Circle background"
                    width={40}
                    height={40}
                    style={{ position: "absolute", top: 0, left: 0 }}
                  />
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    <Image
                      src="/images/components/international/sofa.png"
                      alt="Sofa icon"
                      width={20}
                      height={20}
                      style={{ 
                        width: "auto",
                        height: "auto",
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain"
                      }}
                    />
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#fff",
                    marginBottom: "12px",
                  }}>
                    {t('localMovingVilnius.services.servicesList.houses.title')}
                  </h3>
                  <p style={{
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.services.servicesList.houses.description')}
                  </p>
                </div>
              </div>

              {/* Service Box 3 - Unpacking */}
              <div style={{
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "70px 24px 40px 24px",
                marginBottom: "32px",
                breakInside: "avoid",
                position: "relative",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: isServicesVisible ? 1 : 0,
                transform: isServicesVisible ? 'translateY(0px)' : 'translateY(30px)',  
                transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.8s",
              }}>
                <div style={{
                  position: "absolute",
                  top: "16px",
                  left: "16px",
                  width: "40px",
                  height: "40px",
                }}>
                  <Image
                    src="/images/components/international/red-circle.png"
                    alt="Circle background"
                    width={40}
                    height={40}
                    style={{ position: "absolute", top: 0, left: 0 }}
                  />
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    <Image
                      src="/images/components/international/doc.png"
                      alt="Document icon"
                      width={20}
                      height={20}
                      style={{ 
                        width: "auto",
                        height: "auto",
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain"
                      }}
                    />
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#fff",
                    marginBottom: "12px",
                  }}>
                    {t('localMovingVilnius.services.servicesList.offices.title')}
                  </h3>
                  <p style={{
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.services.servicesList.offices.description')}
                  </p>
                </div>
              </div>

              {/* Service Box 4 - Storage */}
              <div style={{
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "70px 24px 40px 24px",
                marginBottom: "32px",
                breakInside: "avoid",
                position: "relative",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: isServicesVisible ? 1 : 0,
                transform: isServicesVisible ? 'translateX(0px)' : 'translateX(40px)',
                transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.9s",
              }}>
                <div style={{
                  position: "absolute",
                  top: "16px",
                  left: "16px",
                  width: "40px",
                  height: "40px",
                }}>
                  <Image
                    src="/images/components/international/red-circle.png"
                    alt="Circle background"
                    width={40}
                    height={40}
                    style={{ position: "absolute", top: 0, left: 0 }}
                  />
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    <Image
                      src="/images/components/international/house.png"
                      alt="House icon"
                      width={20}
                      height={20}
                      style={{ 
                        width: "auto",
                        height: "auto",
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain"
                      }}
                    />
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#fff",
                    marginBottom: "12px",
                  }}>
                    {t('localMovingVilnius.services.servicesList.students.title')}
                  </h3>
                  <p style={{
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.services.servicesList.students.description')}
                  </p>
                </div>
              </div>

              {/* Service Box 5 - Loading */}
              <div style={{
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "70px 24px 40px 24px",
                marginBottom: "32px",
                breakInside: "avoid",
                position: "relative",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: isServicesVisible ? 1 : 0,
                transform: isServicesVisible ? 'scale(1) translateY(0px)' : 'scale(0.95) translateY(20px)',
                transition: 'all 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "1.0s",
              }}>
                <div style={{
                  position: "absolute",
                  top: "16px",
                  left: "16px",
                  width: "40px",
                  height: "40px",
                }}>
                  <Image
                    src="/images/components/international/red-circle.png"
                    alt="Circle background"
                    width={40}
                    height={40}
                    style={{ position: "absolute", top: 0, left: 0 }}
                  />
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "20px",
                    height: "20px",
                  }}>
                    <div style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                    }}>
                      <Image
                        src="/images/components/international/shield.png"
                        alt="Shield icon"
                        fill
                        unoptimized
                        style={{ 
                          objectFit: "contain"
                        }}
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#fff",
                    marginBottom: "12px",
                  }}>
                    {t('localMovingVilnius.services.servicesList.furniture.title')}
                  </h3>
                  <p style={{
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.services.servicesList.furniture.description')}
                  </p>
                </div>
              </div>

              {/* Service Box 6 - Customs */}
              <div style={{
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "70px 24px 40px 24px",
                marginBottom: "32px",
                breakInside: "avoid",
                position: "relative",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: isServicesVisible ? 1 : 0,
                transform: isServicesVisible ? 'translateX(0px)' : 'translateX(-40px)',
                transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "1.1s",
              }}>
                <div style={{
                  position: "absolute",
                  top: "16px",
                  left: "16px",
                  width: "40px",
                  height: "40px",
                }}>
                  <Image
                    src="/images/components/international/red-circle.png"
                    alt="Circle background"
                    width={40}
                    height={40}
                    style={{ position: "absolute", top: 0, left: 0 }}
                  />
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "20px",
                    height: "20px",
                  }}>
                    <Image
                      src="/images/components/international/settings.png"
                      alt="Settings icon"
                      width={20}
                      height={20}
                    />
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#fff",
                    marginBottom: "12px",
                  }}>
                    {t('localMovingVilnius.services.servicesList.appliances.title')}
                  </h3>
                  <p style={{
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.services.servicesList.appliances.description')}
                  </p>
                </div>
              </div>

              {/* Service Box 7 - Packing */}
              <div style={{
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "70px 24px 40px 24px",
                marginBottom: "32px",
                breakInside: "avoid",
                position: "relative",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: isServicesVisible ? 1 : 0,
                transform: isServicesVisible ? 'translateY(0px)' : 'translateY(30px)',
                transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "1.2s",
              }}>
                <div style={{
                  position: "absolute",
                  top: "16px",
                  left: "16px",
                  width: "40px",
                  height: "40px",
                }}>
                  <Image
                    src="/images/components/international/red-circle.png"
                    alt="Circle background"
                    width={40}
                    height={40}
                    style={{ position: "absolute", top: 0, left: 0 }}
                  />
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "20px",
                    height: "20px",
                  }}>
                    <Image
                      src="/images/components/international/box.png"
                      alt="Box icon"
                      width={20}
                      height={20}
                    />
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#fff",
                    marginBottom: "12px",
                  }}>
                    {t('localMovingVilnius.services.servicesList.singleItems.title')}
                  </h3>
                  <p style={{
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.services.servicesList.singleItems.description')}
                  </p>
                </div>
              </div>

              {/* Service Box 8 - Delivery */}
              <div style={{
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "70px 24px 40px 24px",
                marginBottom: "32px",
                breakInside: "avoid",
                position: "relative",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: isServicesVisible ? 1 : 0,
                transform: isServicesVisible ? 'translateX(0px)' : 'translateX(40px)',
                transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "1.3s",
              }}>
                <div style={{
                  position: "absolute",
                  top: "16px",
                  left: "16px",
                  width: "40px",
                  height: "40px",
                }}>
                  <Image
                    src="/images/components/international/red-circle.png"
                    alt="Circle background"
                    width={40}
                    height={40}
                    style={{ position: "absolute", top: 0, left: 0 }}
                  />
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "20px",
                    height: "20px",
                  }}>
                    <div style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                    }}>
                      <Image
                        src="/images/components/international/door.png"
                        alt="Door icon"
                        fill
                        unoptimized
                        style={{ 
                          objectFit: "contain"
                        }}
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#fff",
                    marginBottom: "12px",
                  }}>
                    {t('localMovingVilnius.services.servicesList.urgent.title')}
                  </h3>
                  <p style={{
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.services.servicesList.urgent.description')}
                  </p>
                </div>
              </div>



            </div>
            

          </div>
          {/* DESTINATIONS SECTION - Red Mercedes Container */}
          <div style={{
            maxWidth: "1400px",
            margin: "0 auto",
            position: "relative",
            zIndex: 10,
            padding: "0 24px",
          }}>
            {/* Main International Moving Destinations Section */}
            <div style={{
              position: "relative",
              marginTop: "80px",
              opacity: isServicesVisible ? 1 : 0,
              transform: isServicesVisible ? 'scale(1) translateY(0px)' : 'scale(0.95) translateY(20px)',
              transition: 'all 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "1.2s",
            }}>
              {/* Blurred Background */}
              <div style={{
                position: "absolute",
                top: "0",
                left: "0",
                right: "0",
                bottom: "0",
                background: "rgba(17, 27, 34, 0.3)",
                border: "1px solid rgba(255, 0, 0, 0.1)",
                borderRadius: "16px",
                zIndex: 1,
              }}></div>

              {/* Left Red Ellipse Behind */}
              <div style={{
                position: "absolute",
                left: "-100px",
                bottom: "250px",
                width: "700px",
                height: "400px",
                background: "radial-gradient(50% 50% at 50% 50%, #D90D34 90%, rgba(217, 13, 52, 0) 100%)",
                filter: "blur(110px)",
                transform: "rotate(-45deg)",
                zIndex: 0,
                opacity: isServicesVisible ? 0.4 : 0,
                transition: "opacity 2s ease-out",
                transitionDelay: "1.5s",
              }}></div>

              {/* Right Red Ellipse Behind */}
              <div style={{
                position: "absolute",
                right: "-100px",
                bottom: "250px",
                width: "700px",
                height: "400px",
                background: "radial-gradient(50% 50% at 50% 50%, #B51F24 70%, rgba(181, 31, 36, 0) 100%)",
                filter: "blur(120px)",
                transform: "rotate(45deg)",
                zIndex: 0,
                opacity: isServicesVisible ? 0.4 : 0,
                transition: "opacity 2s ease-out",
                transitionDelay: "1.8s",
              }}></div>
              
              {/* Content Container */}
              <div style={{
                position: "relative",
                padding: "60px",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "60px",
                alignItems: "center",
                zIndex: 2,
              }}
              className="destinations-grid">
                
                {/* Left side - Red Mercedes Image */}
                <div style={{ 
                  position: "relative",
                  opacity: isServicesVisible ? 1 : 0,
                  transform: isServicesVisible ? 'translateX(0px)' : 'translateX(-40px)',
                  transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  transitionDelay: "1.4s",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                className="destinations-truck-container">
                  {/* MJC Blur Image - Behind the truck */}
                  <div style={{
                    position: "absolute",
                    top: "-40px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 1,
                  }}
                  className="destinations-mjc-blur">
                    <Image
                      src="/images/components/mjc-blur.png"
                      alt="MJC Blur"
                      width={400}
                      height={200}
                      style={{ 
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                  
                  <Image
                    src="/images/components/merc-red.png"
                    alt="International Moving Truck"
                    width={600}
                    height={400}
                    className="destinations-truck-image"
                    style={{ 
                      width: "100%",
                      height: "auto",
                      position: "relative",
                      zIndex: 2,
                      maxWidth: "100%",
                    }}
                  />
                </div>

                {/* Right side - Content */}
                <div style={{
                  opacity: isServicesVisible ? 1 : 0,
                  transform: isServicesVisible ? 'translateX(0px)' : 'translateX(40px)',
                  transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  transitionDelay: "1.6s",
                }}>
                  <h3 style={{
                    fontSize: "32px",
                    fontWeight: 600,
                    color: "#fff",
                    marginBottom: "16px",
                    lineHeight: 1.2,
                  }}>
                    {t('localMovingVilnius.services.districts.title')}
                  </h3>
                  
                                    {/* Single Districts Box */}
                  <div 
                    style={{
                      background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                      border: "1px solid #262626",
                      borderRadius: "12px",
                      padding: "32px",
                      transition: "all 0.3s ease",
                    }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                      <Image
                        src="/images/components/white-pin.png"
                        alt="Location Pin"
                        width={20}
                        height={26}
                        style={{ marginTop: "4px", flexShrink: 0 }}
                      />
                      <div>
                        <p style={{
                          fontSize: "16px",
                          color: "rgba(255,255,255,0.9)",
                          lineHeight: 1.6,
                          margin: 0,
                        }}>
                          {t('localMovingVilnius.services.districts.description')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
                    </div>
        </section>

        {/* WHY CHOOSE US SECTION */}
        <section 
          ref={whyChooseUsRef} 
          style={{
            background: "#000",
            padding: "80px 24px",
            position: "relative",
            overflow: "hidden",
          }}
          className="section-padding"
        >
          {/* Background gradient */}
          <div style={{
            position: "absolute",
            left: "50%",
            top: "300px",
            transform: "translateX(-50%)",
            width: "600px",
            height: "400px",
            background: "radial-gradient(50% 50% at 50% 50%, #D90D34 0%, rgba(217, 13, 52, 0) 100%)",
            filter: "blur(150px)",
            zIndex: 1,
            opacity: isWhyChooseUsVisible ? 0.3 : 0,
            transition: "opacity 1.5s ease-out",
            transitionDelay: "0.5s",
          }} />

          <div style={{
            maxWidth: "1400px",
            margin: "0 auto",
            position: "relative",
            zIndex: 10,
          }}>
            {/* Title and Description */}
            <div style={{ 
              textAlign: "center", 
              marginBottom: "80px",
              opacity: isWhyChooseUsVisible ? 1 : 0,
              transform: isWhyChooseUsVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            }}>
              <h2 style={{
                fontSize: "42px",
                fontWeight: 600,
                color: "#fff",
                lineHeight: 1.2,
                marginBottom: "32px",
              }}
              className="section-title">
                {t('localMovingVilnius.whyChoose.title')}
              </h2>
              
              <p style={{
                fontSize: "18px",
                color: "rgba(255,255,255,0.85)",
                lineHeight: 1.6,
                maxWidth: "800px",
                margin: "0 auto",
                opacity: isWhyChooseUsVisible ? 1 : 0,
                transform: isWhyChooseUsVisible ? 'translateY(0px)' : 'translateY(30px)',
                transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.3s",
              }}>
                {t('localMovingVilnius.whyChoose.intro')}
              </p>
            </div>

            {/* Complex 3x4 Grid for 8 features */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gridTemplateRows: "1fr 1fr 1fr 1fr",
              rowGap: "40px",
              columnGap: "24px",
              minHeight: "950px",
              maxWidth: "900px",
              margin: "0 auto",
              position: "relative",
              opacity: isWhyChooseUsVisible ? 1 : 0,
              transform: isWhyChooseUsVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.5s",
            }}
            className="why-choose-grid">

              {/* Red circle for Box 1 - positioned relative to grid */}
              <div style={{
                position: "absolute",
                top: "-30px",
                left: "20px",
                width: "60px",
                height: "60px",
                zIndex: 10,
              }}>
                <Image
                  src="/images/components/red-circle.png"
                  alt="Red circle"
                  width={60}
                  height={60}
                  style={{ 
                    objectFit: "contain",
                  }}
                />
                {/* Calendar Icon */}
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "28px",
                  height: "28px",
                  zIndex: 11,
                }}>
                  <Image 
                    src="/images/components/international/calendar.png"
                    alt="Calendar icon"
                    width={28}
                    height={28}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                </div>
              </div>

              {/* Box 1 - 1x2 (1 column wide, 2 rows tall) */}
              <div style={{
                gridArea: "1 / 1 / 3 / 2",
                background: "linear-gradient(99.27deg, #000000 10.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "32px 32px 32px 20px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                textAlign: "left",
                position: "relative",
                overflow: "hidden",
                zIndex: 1,
                opacity: isWhyChooseUsVisible ? 1 : 0,
                transform: isWhyChooseUsVisible ? 'scale(1) translateY(0px)' : 'scale(0.95) translateY(20px)',
                transition: 'all 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.2s",
              }}
              className="why-choose-box-1">
                <h3 style={{
                  fontSize: "18px",
                  fontWeight: 400,
                  color: "#fff",
                  marginBottom: "14px",
                  marginTop: "20px",
                }}>
                  {t('localMovingVilnius.whyChoose.features.knowledge.title')}
                </h3>
                <p style={{
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.5,
                  marginBottom: "24px",
                }}>
                  {t('localMovingVilnius.whyChoose.features.knowledge.description')}
                </p>
                
                {/* MJC Blur Image */}
                <div style={{
                  width: "350%",
                  height: "260px",
                  position: "absolute",
                  bottom: "-40px",
                  left: "-150%",
                  zIndex: 0,
                }}
                className="mjc-blur-mobile">
                  <Image
                    src="/images/components/mjc-blur.png"
                    alt="MJC Blur"
                    fill
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                </div>
              </div>

              {/* Box 2 - 2x1 (2 columns wide, 1 row tall) */}
              <div style={{
                gridArea: "1 / 2 / 2 / 4",
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "32px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                textAlign: "left",
                position: "relative",
                opacity: isWhyChooseUsVisible ? 1 : 0,
                transform: isWhyChooseUsVisible ? 'translateX(0px)' : 'translateX(40px)',
                transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.4s",
              }}
              className="why-choose-box-2">
                {/* Red circle positioned outside the top border */}
                <div style={{
                  position: "absolute",
                  top: "-30px",
                  left: "20px",
                  width: "60px",
                  height: "60px",
                  zIndex: 2,
                }}>
                  <Image
                    src="/images/components/red-circle.png"
                    alt="Red circle"
                    width={60}
                    height={60}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                  {/* Network Icon */}
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "28px",
                    height: "28px",
                    zIndex: 3,
                  }}>
                    <Image 
                      src="/images/components/international/network.png"
                      alt="Network icon"
                      width={28}
                      height={28}
                      style={{ 
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>
                
                {/* Left side - Text content */}
                <div style={{ 
                  flex: 1, 
                  paddingRight: "32px",
                  maxWidth: "calc(100% - 260px)",
                  zIndex: 2,
                  opacity: isWhyChooseUsVisible ? 1 : 0,
                  transform: isWhyChooseUsVisible ? 'translateX(0px)' : 'translateX(-40px)',
                  transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  transitionDelay: "0.8s",
                }}
                className="text-content">
                  <h3 style={{
                    fontSize: "18px",
                    fontWeight: 400,
                    color: "#fff",
                    marginBottom: "14px",
                    marginTop: "20px",
                  }}>
                    {t('localMovingVilnius.whyChoose.features.speed.title')}
                  </h3>
                  <p style={{
                    fontSize: "12px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.whyChoose.features.speed.description')}
                  </p>
                </div>

                {/* Right side - World image */}
                <div style={{
                  width: "280px",
                  height: "200px",
                  position: "absolute",
                  bottom: "1px",
                  right: "-20px",
                  flexShrink: 0,
                  zIndex: 1,
                  opacity: isWhyChooseUsVisible ? 1 : 0,
                  transform: isWhyChooseUsVisible ? 'translateX(0px)' : 'translateX(40px)',
                  transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  transitionDelay: "1s",
                }}
                className="world-container">
                  <Image
                    src="/images/components/international/world.png"
                    alt="World map"
                    fill
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                  
                  {/* Glowing red dots */}
                  <div style={{
                    position: "absolute",
                    top: "30%",
                    left: "25%",
                    width: "6px",
                    height: "6px",
                    backgroundColor: "#D90D34",
                    borderRadius: "50%",
                    boxShadow: "0 0 8px #D90D34, 0 0 12px #D90D34, 0 0 16px #D90D34",
                    zIndex: 2,
                  }} />
                  
                  <div style={{
                    position: "absolute",
                    top: "60%",
                    left: "70%",
                    width: "6px",
                    height: "6px",
                    backgroundColor: "#D90D34",
                    borderRadius: "50%",
                    boxShadow: "0 0 8px #D90D34, 0 0 12px #D90D34, 0 0 16px #D90D34",
                    zIndex: 2,
                  }} />
                  
                  <div style={{
                    position: "absolute",
                    top: "45%",
                    left: "50%",
                    width: "6px",
                    height: "6px",
                    backgroundColor: "#D90D34",
                    borderRadius: "50%",
                    boxShadow: "0 0 8px #D90D34, 0 0 12px #D90D34, 0 0 16px #D90D34",
                    zIndex: 2,
                  }} />
                </div>
              </div>

              {/* Box 3 - 1x1 (square) */}
              <div style={{
                gridArea: "2 / 2 / 3 / 3",
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "32px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                textAlign: "left",
                position: "relative",
                opacity: isWhyChooseUsVisible ? 1 : 0,
                transform: isWhyChooseUsVisible ? 'translateY(0px)' : 'translateY(30px)',
                transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.6s",
              }}>
                {/* Red circle positioned outside the top border */}
                <div style={{
                  position: "absolute",
                  top: "-30px",
                  left: "20px",
                  width: "60px",
                  height: "60px",
                  zIndex: 2,
                }}>
                  <Image
                    src="/images/components/red-circle.png"
                    alt="Red circle"
                    width={60}
                    height={60}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                  {/* Safety Icon */}
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "28px",
                    height: "28px",
                    zIndex: 3,
                  }}>
                    <Image 
                      src="/images/components/international/safety.png"
                      alt="Safety icon"
                      width={28}
                      height={28}
                      style={{ 
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: "18px",
                    fontWeight: 400,
                    color: "#fff",
                    marginBottom: "14px",
                    marginTop: "20px",
                  }}>
                    {t('localMovingVilnius.whyChoose.features.safety.title')}
                  </h3>
                  <p style={{
                    fontSize: "12px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.whyChoose.features.safety.description')}
                  </p>
                </div>
              </div>

              {/* Box 4 - 1x1 (square) */}
              <div style={{
                gridArea: "2 / 3 / 3 / 4",
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "32px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                textAlign: "left",
                position: "relative",
                opacity: isWhyChooseUsVisible ? 1 : 0,
                transform: isWhyChooseUsVisible ? 'translateY(0px)' : 'translateY(30px)',
                transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.8s",
              }}>
                {/* Red circle positioned outside the top border */}
                <div style={{
                  position: "absolute",
                  top: "-30px",
                  left: "20px",
                  width: "60px",
                  height: "60px",
                  zIndex: 2,
                }}>
                  <Image
                    src="/images/components/red-circle.png"
                    alt="Red circle"
                    width={60}
                    height={60}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                  {/* Gear Icon */}
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "28px",
                    height: "28px",
                    zIndex: 3,
                  }}>
                    <Image 
                      src="/images/components/international/gear.png"
                      alt="Gear icon"
                      width={28}
                      height={28}
                      style={{ 
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: "18px",
                    fontWeight: 400,
                    color: "#fff",
                    marginBottom: "14px",
                    marginTop: "20px",
                  }}>
                    {t('localMovingVilnius.whyChoose.features.allInOne.title')}
                  </h3>
                  <p style={{
                    fontSize: "12px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.whyChoose.features.allInOne.description')}
                  </p>
                </div>
              </div>

              {/* Box 5 - 2x1 (2 columns wide, 1 row tall) */}
              <div style={{
                gridArea: "3 / 1 / 4 / 3",
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "32px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                textAlign: "left",
                position: "relative",
                opacity: isWhyChooseUsVisible ? 1 : 0,
                transform: isWhyChooseUsVisible ? 'translateX(0px)' : 'translateX(-40px)',
                transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "1s",
              }}>
                {/* Red circle positioned outside the top border */}
                <div style={{
                  position: "absolute",
                  top: "-30px",
                  left: "20px",
                  width: "60px",
                  height: "60px",
                  zIndex: 2,
                }}>
                  <Image
                    src="/images/components/red-circle.png"
                    alt="Red circle"
                    width={60}
                    height={60}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                  {/* Network Icon */}
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "28px",
                    height: "28px",
                    zIndex: 3,
                  }}>
                    <Image 
                      src="/images/components/international/network.png"
                      alt="Network icon"
                      width={28}
                      height={28}
                      style={{ 
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: "18px",
                    fontWeight: 400,
                    color: "#fff",
                    marginBottom: "14px",
                    marginTop: "20px",
                  }}>
                    {t('localMovingVilnius.whyChoose.features.attention.title')}
                  </h3>
                  <p style={{
                    fontSize: "12px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.whyChoose.features.attention.description')}
                  </p>
                </div>
              </div>

              {/* Box 6 - 1x1 (square) */}
              <div style={{
                gridArea: "3 / 3 / 4 / 4",
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "32px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                textAlign: "left",
                position: "relative",
                opacity: isWhyChooseUsVisible ? 1 : 0,
                transform: isWhyChooseUsVisible ? 'scale(1) translateY(0px)' : 'scale(0.95) translateY(20px)',
                transition: 'all 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "1.2s",
              }}>
                {/* Red circle positioned outside the top border */}
                <div style={{
                  position: "absolute",
                  top: "-30px",
                  left: "20px",
                  width: "60px",
                  height: "60px",
                  zIndex: 2,
                }}>
                  <Image
                    src="/images/components/red-circle.png"
                    alt="Red circle"
                    width={60}
                    height={60}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                  {/* Transparency Icon */}
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "28px",
                    height: "28px",
                    zIndex: 3,
                  }}>
                    <Image 
                      src="/images/components/international/transparency.png"
                      alt="Transparency icon"
                      width={28}
                      height={28}
                      style={{ 
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: "18px",
                    fontWeight: 400,
                    color: "#fff",
                    marginBottom: "14px",
                    marginTop: "20px",
                  }}>
                    {t('localMovingVilnius.whyChoose.features.price.title')}
                  </h3>
                  <p style={{
                    fontSize: "12px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.whyChoose.features.price.description')}
                  </p>
                </div>
              </div>

              {/* Box 7 - 1x1 (square) for flexibility feature */}
              <div style={{
                gridArea: "4 / 1 / 5 / 2",
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "32px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                textAlign: "left",
                position: "relative",
                opacity: isWhyChooseUsVisible ? 1 : 0,
                transform: isWhyChooseUsVisible ? 'scale(1) translateY(0px)' : 'scale(0.95) translateY(20px)',
                transition: 'all 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "1.4s",
              }}>
                {/* Red circle positioned outside the top border */}
                <div style={{
                  position: "absolute",
                  top: "-30px",
                  left: "20px",
                  width: "60px",
                  height: "60px",
                  zIndex: 2,
                }}>
                  <Image
                    src="/images/components/red-circle.png"
                    alt="Red circle"
                    width={60}
                    height={60}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                  {/* Clock Icon for flexibility */}
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "28px",
                    height: "28px",
                    zIndex: 3,
                  }}>
                    <Image 
                      src="/images/components/international/calendar.png"
                      alt="Calendar icon"
                      width={28}
                      height={28}
                      style={{ 
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: "18px",
                    fontWeight: 400,
                    color: "#fff",
                    marginBottom: "14px",
                    marginTop: "20px",
                  }}>
                    {t('localMovingVilnius.whyChoose.features.flexibility.title')}
                  </h3>
                  <p style={{
                    fontSize: "12px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.whyChoose.features.flexibility.description')}
                  </p>
                </div>
              </div>

              {/* Box 8 - 1x1 (square) for team feature */}
              <div style={{
                gridArea: "4 / 2 / 5 / 3",
                background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                border: "1px solid #262626",
                borderRadius: "12px",
                padding: "32px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                textAlign: "left",
                position: "relative",
                opacity: isWhyChooseUsVisible ? 1 : 0,
                transform: isWhyChooseUsVisible ? 'scale(1) translateY(0px)' : 'scale(0.95) translateY(20px)',
                transition: 'all 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "1.6s",
              }}>
                {/* Red circle positioned outside the top border */}
                <div style={{
                  position: "absolute",
                  top: "-30px",
                  left: "20px",
                  width: "60px",
                  height: "60px",
                  zIndex: 2,
                }}>
                  <Image
                    src="/images/components/red-circle.png"
                    alt="Red circle"
                    width={60}
                    height={60}
                    style={{ 
                      objectFit: "contain",
                    }}
                  />
                  {/* Team Icon */}
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "28px",
                    height: "28px",
                    zIndex: 3,
                  }}>
                    <Image 
                      src="/images/components/international/network.png"
                      alt="Team icon"
                      width={28}
                      height={28}
                      style={{ 
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: "18px",
                    fontWeight: 400,
                    color: "#fff",
                    marginBottom: "14px",
                    marginTop: "20px",
                  }}>
                    {t('localMovingVilnius.whyChoose.features.team.title')}
                  </h3>
                  <p style={{
                    fontSize: "12px",
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.5,
                  }}>
                    {t('localMovingVilnius.whyChoose.features.team.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROCESS SECTION */}
        <section 
          ref={processRef} 
          style={{
            background: "#000",
            padding: "80px 24px 40px 24px",
            position: "relative",
          }}
          className="section-padding"
        >
          {/* Background gradients */}
          <div style={{
            position: "absolute",
            left: "100px",
            top: "300px",
            transform: "rotate(-135deg)",
            width: "400px",
            height: "400px",
            background: "linear-gradient(135deg,rgb(255, 0, 21) 70%, #090909 100%)",
            borderRadius: "50px",
            filter: "blur(300px)",
            zIndex: 1,
            pointerEvents: "none",
            opacity: isProcessVisible ? 1 : 0,
            transition: "opacity 2s ease-out",
            transitionDelay: "0.5s",
          }} />

          <div style={{
            position: "absolute",
            right: "100px",
            top: "300px",
            transform: "rotate(135deg)",
            width: "400px",
            height: "400px",
            background: "linear-gradient(135deg,rgb(255, 0, 21) 70%, #090909 100%)",
            borderRadius: "50px",
            filter: "blur(300px)",
            zIndex: 1,
            pointerEvents: "none",
            opacity: isProcessVisible ? 1 : 0,
            transition: "opacity 2s ease-out",
            transitionDelay: "1s",
          }} />

          <div style={{
            maxWidth: "1000px",
            margin: "0 auto",
            position: "relative",
            zIndex: 2,
          }}>
            {/* Content Grid */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "60px",
              alignItems: "flex-start",
              opacity: isProcessVisible ? 1 : 0,
              transform: isProcessVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.3s",
            }}
            className="process-grid">

              {/* Left Side - Title, Description & Image */}
              <div style={{
                display: "flex",
                flexDirection: "column",
                opacity: isProcessVisible ? 1 : 0,
                transform: isProcessVisible ? 'translateX(0px)' : 'translateX(-40px)',
                transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.5s",
              }}>
                <div>
                  <h2 style={{
                    fontSize: "36px",
                    fontWeight: 600,
                    color: "#fff",
                    lineHeight: 1.2,
                    marginBottom: "16px",
                    textAlign: "left",
                  }}
                  className="section-title">
                    {t('localMovingVilnius.process.title')}
                  </h2>

                  <p style={{
                    fontSize: "16px",
                    color: "rgba(255,255,255,0.8)",
                    lineHeight: 1.6,
                    textAlign: "left",
                    marginBottom: "40px",
                    opacity: isProcessVisible ? 1 : 0,
                    transform: isProcessVisible ? 'translateY(0px)' : 'translateY(30px)',
                    transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    transitionDelay: "0.7s",
                  }}>
                    {t('localMovingVilnius.process.intro')}
                  </p>
                </div>

                {/* Image */}
                <div style={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "400px",
                  opacity: isProcessVisible ? 1 : 0,
                  transform: isProcessVisible ? 'scale(1)' : 'scale(0.95)',
                  transition: 'all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  transitionDelay: "0.9s",
                }}>
                  <div style={{
                    position: "relative",
                    width: "320px",
                    height: "400px",
                  }}>
                    <Image 
                      src="/images/components/phone-email.png"
                      alt="International moving process illustration"
                      fill
                      style={{ 
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Right Side - Accordions */}
              <div style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                opacity: isProcessVisible ? 1 : 0,
                transform: isProcessVisible ? 'translateX(0px)' : 'translateX(40px)',
                transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.8s",
              }}>
                
                {/* Create all 10 process steps */}
                {[
                  { key: 0, index: 0 },
                  { key: 1, index: 1 },
                  { key: 2, index: 2 },
                  { key: 3, index: 3 },
                  { key: 4, index: 4 },
                  { key: 5, index: 5 },
                  { key: 6, index: 6 },
                  { key: 7, index: 7 },
                  { key: 8, index: 8 },
                  { key: 9, index: 9 }
                ].map(({ key, index }) => (
                  <div 
                    key={index}
                    style={{
                      border: activeAccordion === index ? "none" : "1px solid #262626",
                      borderRadius: "12px",
                      background: activeAccordion === index 
                        ? "linear-gradient(135deg, #000000 0%, #880105 100%)" 
                        : "linear-gradient(135deg, #000000 0%, #0b0b0b 100%)",
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <button
                      onClick={() => setActiveAccordion(activeAccordion === index ? null : index)}
                      style={{
                        width: "100%",
                        padding: "16px 20px",
                        background: "none",
                        border: "none",
                        color: "#fff",
                        fontSize: "14px",
                        fontWeight: 600,
                        textAlign: "left",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        transition: "color 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (activeAccordion !== index) {
                          e.currentTarget.style.color = "rgba(255,255,255,0.9)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "#fff";
                      }}
                    >
                      <div style={{ 
                        display: "flex",
                        alignItems: "center",
                        gap: "12px" 
                      }}>
                        {/* Circle with Number */}
                        <div style={{
                          position: "relative",
                          width: "32px",
                          height: "32px",
                          flexShrink: 0,
                        }}>
                          <Image 
                            src={activeAccordion === index 
                              ? "/images/components/red-circle.png" 
                              : "/images/components/grey-circle.png"
                            }
                            alt="Circle"
                            fill
                            style={{ 
                              objectFit: "contain",
                            }}
                          />
                          {/* Number inside circle */}
                          <div style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            color: "#fff",
                            fontSize: "14px",
                            fontWeight: 700,
                            zIndex: 1,
                          }}>
                            {index + 1}
                          </div>
                        </div>
                        <span>{t(`localMovingVilnius.process.steps.${key}.title`)}</span>
                      </div>
                      <span style={{
                        fontSize: "16px",
                        transform: activeAccordion === index ? "rotate(-180deg)" : "rotate(0deg)",
                        transition: "transform 0.3s ease",
                      }}>
                        ▼
                      </span>
                    </button>

                    {activeAccordion === index && (
                      <div style={{
                        padding: "0 20px 20px 20px",
                        animation: "fadeIn 0.3s ease",
                      }}>
                        <div style={{
                          fontSize: "12px",
                          color: "rgba(255,255,255,0.8)",
                          lineHeight: 1.5,
                          paddingLeft: "44px", // Align with title text (32px circle + 12px gap)
                        }}>
                          <div style={{
                            display: "flex",
                            alignItems: "flex-start",
                            marginBottom: "10px",
                          }}>
                            <div style={{
                              width: "12px",
                              height: "12px",
                              marginRight: "10px",
                              marginTop: "2px",
                              flexShrink: 0,
                              position: "relative"
                            }}>
                              <Image 
                                src="/images/components/check-2.png"
                                alt="Check mark"
                                fill
                                style={{ objectFit: "contain" }}
                              />
                            </div>
                            <span>{t(`localMovingVilnius.process.steps.${key}.description`)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

              </div>
            </div>
          </div>
        </section>

        {/* FAQ SECTION */}
        <section 
          ref={faqRef} 
          style={{
            background: "#000",
            padding: "80px 24px",
            position: "relative",
          }}
          className="section-padding"
        >
          <div style={{
            maxWidth: "800px",
            margin: "0 auto",
            opacity: isFAQVisible ? 1 : 0,
            transform: isFAQVisible ? 'translateY(0px)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: "0.3s",
          }}>
            {/* Header */}
            <div style={{
              textAlign: "center",
              marginBottom: "60px",
            }}>
              <h2 style={{
                fontSize: "36px",
                fontWeight: 600,
                color: "#fff",
                lineHeight: 1.2,
                marginBottom: "24px",
              }}
              className="section-title">
                {t('localMovingVilnius.faq.title')}
              </h2>

              <p style={{
                fontSize: "18px",
                color: "rgba(255,255,255,0.8)",
                lineHeight: 1.6,
                maxWidth: "600px",
                margin: "0 auto",
                opacity: isFAQVisible ? 1 : 0,
                transform: isFAQVisible ? 'translateY(0px)' : 'translateY(30px)',
                transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.5s",
              }}>
                {t('localMovingVilnius.faq.intro')}
              </p>
            </div>

            {/* FAQ Items */}
            <div style={{
              display: "flex",
              flexDirection: "column",
            }}>
              {/* All 5 FAQ Items */}
              {[
                { key: 'price', index: 0 },
                { key: 'district', index: 1 },
                { key: 'oldTown', index: 2 },
                { key: 'duration', index: 3 },
                { key: 'materials', index: 4 }
              ].map(({ key, index }) => (
                <div key={index}>
                  <div style={{
                    opacity: isFAQVisible ? 1 : 0,
                    transform: isFAQVisible ? 'translateY(0px)' : 'translateY(30px)',
                    transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    transitionDelay: `${0.7 + (index * 0.1)}s`,
                  }}>
                    <div style={{
                      padding: "24px 0",
                    }}>
                      <button
                        onClick={() => setActiveFAQ(activeFAQ === index ? null : index)}
                        style={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          background: "none",
                          border: "none",
                          color: "#fff",
                          fontSize: "18px",
                          fontWeight: 500,
                          textAlign: "left",
                          cursor: "pointer",
                          padding: "0",
                          lineHeight: 1.4,
                        }}
                      >
                        <span style={{ marginRight: "20px" }}>
                          {t(`localMovingVilnius.faq.questions.${key}.question`)}
                        </span>
                        
                        <div style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          border: "2px solid #262626",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          transition: "all 0.3s ease",
                          backgroundColor: activeFAQ === index ? "#D90D34" : "transparent",
                          borderColor: activeFAQ === index ? "#D90D34" : "#262626",
                        }}>
                          <span style={{
                            fontSize: "20px",
                            fontWeight: 300,
                            color: "#fff",
                            lineHeight: 1,
                            transition: "transform 0.3s ease",
                            transform: activeFAQ === index ? "rotate(45deg)" : "rotate(0deg)",
                          }}>
                            +
                          </span>
                        </div>
                      </button>

                      {activeFAQ === index && (
                        <div style={{
                          marginTop: "20px",
                          paddingRight: "60px",
                          animation: "fadeIn 0.3s ease",
                        }}>
                          <p style={{
                            fontSize: "16px",
                            color: "rgba(255,255,255,0.8)",
                            lineHeight: 1.6,
                            margin: 0,
                          }}>
                            {t(`localMovingVilnius.faq.questions.${key}.answer`)}
                          </p>
                        </div>
                      )}
                    </div>

                    {index < 4 && (
                      <div style={{
                        height: "1px",
                        background: "#262626",
                        width: "100%",
                      }} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CONTACT US SECTION */}
        <section 
          ref={contactRef} 
          style={{
            background: "#000",
            padding: "80px 24px",
            position: "relative",
            overflow: "hidden",
          }}
          className="section-padding"
        >
          {/* Black Square at top center */}
          <div style={{
            position: "absolute",
            top: "-30px",
            left: "50%",
            transform: "translateX(-50%) rotate(45deg)",
            width: "60px",
            height: "60px",
            background: "#000",
            zIndex: 2,
          }}></div>

          {/* Background Rectangle with blur */}
          <div style={{
            position: "absolute",
            top: "30%",
            left: "40%",
            transform: "translate(-50%, -50%) rotate(25deg)",
            width: "1200px",
            height: "200px",
            background: "linear-gradient(135deg,rgb(167, 2, 8) 100%, rgba(140, 7, 25, 0.3) 100%)",
            borderRadius: "10px",
            filter: "blur(170px)",
            zIndex: 0,
            pointerEvents: "none",
            opacity: isContactVisible ? 1 : 0,
            transition: "opacity 2s ease-out",
            transitionDelay: "0.5s",
          }}></div>

          <div style={{
            maxWidth: "800px",
            margin: "0 auto",
            textAlign: "center",
            position: "relative",
            zIndex: 1,
          }}>
            {/* Main Text */}
            <h2 style={{
              fontSize: "48px",
              fontWeight: 600,
              color: "#fff",
              lineHeight: 1.2,
              marginBottom: "30px",
              opacity: isContactVisible ? 1 : 0,
              transform: isContactVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.3s",
            }}
            className="contact-title">
              {t('localMovingVilnius.contact.title')}
            </h2>

            <p style={{
              fontSize: "24px",
              color: "rgba(255,255,255,0.9)",
              lineHeight: 1.4,
              marginBottom: "50px",
              opacity: isContactVisible ? 1 : 0,
              transform: isContactVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.5s",
            }}
            className="contact-description">
              {t('localMovingVilnius.contact.description')}
            </p>

            {/* Contact Us Button */}
            <a href="#" style={{
              display: "inline-flex",
              alignItems: "center",
              background: "rgba(50, 50, 50, 0.5)",
              color: "#fff",
              padding: "12px 18px",
              fontSize: "12px",
              fontWeight: 400,
              borderRadius: "9px",
              textDecoration: "none",
              textTransform: "uppercase",
              gap: "12px",
              opacity: isContactVisible ? 1 : 0,
              transform: isContactVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.7s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 25px rgba(50, 50, 50, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0px)";
              e.currentTarget.style.boxShadow = "none";
            }}
            >
              {t('contact.button') || 'Contact Us'}
              <div style={{
                width: "32px",
                height: "32px",
                background: "#B51F24",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: "20px",
                fontWeight: "bold",
              }}>
                <Image 
                  src="/images/components/arrow.png" 
                  alt="Arrow" 
                  width={7}
                  height={7}
                  style={{ filter: "brightness(0) invert(1)" }}
                />
              </div>
            </a>
          </div>
        </section>

      <Footer />
    </main>
    </>
  );
} 